import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Chapter = ({ number, kicker, title, description, points, image, imageAlt, reverse = false }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <section
      ref={ref}
      data-testid={`chapter-${number}`}
      className={`grid lg:grid-cols-2 gap-14 lg:gap-20 items-center px-6 sm:px-10 lg:px-16 py-24 lg:py-36 border-b border-[#1A1A1A]/[0.08] ${
        reverse ? "lg:[&>*:first-child]:order-2" : ""
      }`}
    >
      <div>
        <motion.p
          variants={reveal}
          custom={0}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          data-testid={`chapter-${number}-number`}
          className="font-display font-light text-6xl sm:text-8xl tracking-tighter text-[#FF6B35]/90 leading-none"
        >
          {number}
        </motion.p>
        <motion.p
          variants={reveal}
          custom={1}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-6 text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#5C5C5C]"
        >
          {kicker}
        </motion.p>
        <motion.h2
          variants={reveal}
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          data-testid={`chapter-${number}-title`}
          className="font-display font-bold mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1A1A1A]"
        >
          {title}
        </motion.h2>
        <motion.p
          variants={reveal}
          custom={3}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-6 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-lg"
        >
          {description}
        </motion.p>
        <motion.ul
          variants={reveal}
          custom={4}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-8 space-y-3"
        >
          {points.map((p, i) => (
            <li key={i} className="flex items-center gap-3 text-sm sm:text-base font-medium text-[#1A1A1A]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B35] shrink-0" />
              {p}
            </li>
          ))}
        </motion.ul>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="relative"
      >
        <div
          className={`absolute -inset-5 rounded-[2.5rem] bg-[#FF6B35]/10 ${reverse ? "rotate-2" : "-rotate-2"}`}
        />
        <div className="relative overflow-hidden rounded-[2.5rem] border border-[#1A1A1A]/10 shadow-[0_30px_80px_rgba(26,26,26,0.12)] h-[26rem] sm:h-[32rem]">
          <motion.img
            src={image}
            alt={imageAlt}
            style={{ y: imgY }}
            data-testid={`chapter-${number}-image`}
            className="w-full h-[calc(100%+100px)] object-cover will-change-transform"
          />
        </div>
      </motion.div>
    </section>
  );
};
